import { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '@/api/client';
import CreatePortraitModal, { type EmployerQuestion } from '@/pages/portrait/components/CreatePortraitModal';

interface Profile {
  id: string;
  name: string;
  answers: Record<string, string | number>;
  ai_summary?: string | null;
  created_at?: string;
}

const dimensionColors = [
  'bg-primary-50 text-primary-700 border-primary-200',
  'bg-accent-50 text-accent-700 border-accent-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-emerald-50 text-emerald-700 border-emerald-200',
  'bg-purple-50 text-purple-700 border-purple-200',
  'bg-secondary-50 text-secondary-700 border-secondary-200',
];

export default function Portrait() {
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [questions, setQuestions] = useState<EmployerQuestion[]>([]);
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(null);
  const [selectedProfile, setSelectedProfile] = useState<Profile | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef<number | null>(null);

  const loadProfiles = useCallback(async () => {
    const list = await api.get<Profile[]>('/employer-profiles');
    setProfiles(list);
    return list;
  }, []);

  useEffect(() => {
    Promise.all([
      loadProfiles(),
      api.get<EmployerQuestion[]>('/employer-profile/questions'),
    ]).then(([list, qs]) => {
      setQuestions(qs);
      if (list.length > 0) setSelectedProfileId(list[0].id);
    }).finally(() => setLoading(false));
  }, [loadProfiles]);

  // Загрузка выбранного профиля + поллинг, пока AI-портрет генерируется
  useEffect(() => {
    if (!selectedProfileId) { setSelectedProfile(null); return; }
    let cancelled = false;

    const fetchProfile = async () => {
      try {
        const p = await api.get<Profile>(`/employer-profiles/${selectedProfileId}`);
        if (cancelled) return;
        setSelectedProfile(p);
        if (!p.ai_summary) {
          pollRef.current = window.setTimeout(fetchProfile, 4000);
        }
      } catch { /* ignore */ }
    };
    fetchProfile();

    return () => {
      cancelled = true;
      if (pollRef.current) window.clearTimeout(pollRef.current);
    };
  }, [selectedProfileId]);

  const handleCreated = async (profile: Profile) => {
    setShowCreateModal(false);
    await loadProfiles();
    setSelectedProfileId(profile.id);
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('Удалить портрет? Совместимость по нему больше нельзя будет считать.')) return;
    try {
      await api.del(`/employer-profiles/${id}`);
      const list = await loadProfiles();
      setSelectedProfileId(list[0]?.id || null);
    } catch (err) {
      alert((err as Error).message || 'Ошибка удаления');
    }
  };

  // Группировка вопросов по измерениям
  const dimensions: { key: string; label: string; items: EmployerQuestion[] }[] = [];
  for (const q of questions) {
    let d = dimensions.find((x) => x.key === q.dimension);
    if (!d) {
      d = { key: q.dimension, label: q.dimension_label, items: [] };
      dimensions.push(d);
    }
    d.items.push(q);
  }

  const answerLabel = (q: EmployerQuestion, value: string | number | undefined): string => {
    if (value === undefined || value === null || value === '') return '—';
    if (q.type === 'choice' && q.options) {
      return q.options.find((o) => o.value === String(value))?.label || String(value);
    }
    if (q.type === 'scale') {
      return `${value}/5${q.scale_left && q.scale_right ? ` (${q.scale_left} ↔ ${q.scale_right})` : ''}`;
    }
    return String(value);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="w-8 h-8 border-2 border-primary-300 border-t-primary-500 rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl md:text-2xl font-heading font-bold text-foreground-950">Портрет руководителя</h1>
          <p className="text-sm text-foreground-500 mt-1">
            AI использует портрет для оценки совместимости кандидатов с руководителем
          </p>
        </div>
        <div className="flex items-center gap-3">
          {profiles.length > 0 && (
            <select
              value={selectedProfileId || ''}
              onChange={(e) => setSelectedProfileId(e.target.value)}
              className="px-4 py-2.5 rounded-xl bg-white border border-background-200 text-sm text-foreground-700 focus:outline-none focus:ring-2 focus:ring-primary-400 cursor-pointer"
            >
              {profiles.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <button
            onClick={() => setShowCreateModal(true)}
            className="text-sm flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-primary-500 text-white font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-add-line" />
            Новый портрет
          </button>
        </div>
      </div>

      {profiles.length === 0 && (
        <div className="bg-white rounded-xl border border-background-200 p-10 text-center">
          <p className="text-sm text-foreground-500 mb-4">Портретов пока нет. Создайте первый — ответьте на 15 вопросов о вашем стиле управления, и AI сформирует портрет для оценки совместимости кандидатов.</p>
        </div>
      )}

      {selectedProfile && (
        <>
          {/* AI Summary */}
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <i className="ri-sparkling-line text-amber-600"></i>
                <p className="text-sm font-semibold text-amber-900">AI-портрет: {selectedProfile.name}</p>
              </div>
              <button
                onClick={() => handleDelete(selectedProfile.id)}
                className="text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer"
              >
                <i className="ri-delete-bin-line mr-1" />Удалить
              </button>
            </div>
            {selectedProfile.ai_summary ? (
              <p className="text-sm text-amber-800 leading-relaxed whitespace-pre-wrap">{selectedProfile.ai_summary}</p>
            ) : (
              <p className="text-sm text-amber-700 flex items-center gap-2">
                <i className="ri-loader-4-line animate-spin" />
                AI формирует портрет по вашим ответам — обычно это занимает меньше минуты...
              </p>
            )}
          </div>

          {/* Answers grouped by dimension */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {dimensions.map((dim, di) => (
              <div key={dim.key} className="bg-white rounded-xl border border-background-200 p-5">
                <span className={`inline-flex text-xs font-medium px-2.5 py-1 rounded-full border mb-4 ${dimensionColors[di % dimensionColors.length]}`}>
                  {dim.label}
                </span>
                <div className="space-y-3">
                  {dim.items.map((q) => (
                    <div key={q.id}>
                      <p className="text-xs text-foreground-500 mb-0.5">{q.question}</p>
                      <p className="text-sm font-medium text-foreground-800">{answerLabel(q, selectedProfile.answers?.[q.id])}</p>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {showCreateModal && (
        <CreatePortraitModal
          questions={questions}
          onClose={() => setShowCreateModal(false)}
          onSave={handleCreated}
        />
      )}
    </div>
  );
}
